import React, { useState, useEffect, useRef } from 'react';
import PageSEO from '../components/PageSEO';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const SESSION_KEY = 'apex_chat_session';

const QUICK_QUESTIONS = [
  'Quels travaux proposez-vous ?',
  'Quel est le prix d\'une rénovation de salle de bain ?',
  'Intervenez-vous à Bourges ?',
  'Quand pouvez-vous commencer les travaux ?',
  'Comment fonctionne la visite gratuite ?',
  'Je veux rénover un appartement avant de le louer',
];

const MessageBubble = React.forwardRef(function MessageBubble({ msg }, ref) {
  const isUser = msg.role === 'user';
  return (
    <div ref={ref} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-xs shrink-0 mr-2 mt-1">
          A
        </div>
      )}
      <div
        className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
          isUser
            ? 'bg-slate-800 text-white rounded-tr-sm'
            : 'bg-white border border-slate-200 text-slate-800 rounded-tl-sm shadow-sm'
        }`}
      >
        {msg.content.split('\n').map((line, i, arr) => (
          <span key={i}>
            <span dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
            {i < arr.length - 1 && <br />}
          </span>
        ))}
      </div>
      {isUser && (
        <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-white text-xs shrink-0 ml-2 mt-1">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
          </svg>
        </div>
      )}
    </div>
  );
});

function TypingIndicator() {
  return (
    <div className="flex justify-start mb-3">
      <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-xs shrink-0 mr-2 mt-1">
        A
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
        <div className="flex gap-1.5 items-center">
          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}

const WaIcon = () => (
  <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.533 5.858L.054 23.445a.75.75 0 00.922.922l5.587-1.479A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.907 0-3.7-.506-5.248-1.391l-.378-.22-3.917 1.036 1.036-3.917-.22-.378A9.952 9.952 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
  </svg>
);

export default function Assistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [started, setStarted] = useState(false);
  const [error, setError] = useState('');
  const chatMobileRef = useRef(null);
  const chatDesktopRef = useRef(null);
  const inputRef = useRef(null);
  const messageCountRef = useRef(0);
  const lastMsgRef = useRef(null);

  // Restore or create session
  useEffect(() => {
    const saved = localStorage.getItem(SESSION_KEY);
    if (saved) setSessionId(saved);
  }, []);

  function scrollChat() {
    [chatMobileRef, chatDesktopRef].forEach(ref => {
      const el = ref.current;
      if (!el || el.scrollHeight <= el.clientHeight) return;
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
  }

  useEffect(() => {
    const newCount = messages.length;
    if (newCount > messageCountRef.current) {
      messageCountRef.current = newCount;
      const lastMsg = messages[newCount - 1];
      if (lastMsg?.role === 'assistant' && lastMsgRef.current) {
        // Scroll to top of agent reply, not bottom
        [chatMobileRef, chatDesktopRef].forEach(containerRef => {
          const container = containerRef.current;
          const el = lastMsgRef.current;
          if (!container || !el) return;
          container.scrollTo({ top: el.offsetTop - container.offsetTop - 8, behavior: 'smooth' });
        });
      } else {
        scrollChat();
      }
    }
  }, [messages]);

  useEffect(() => {
    if (loading) {
      scrollChat();
    } else {
      inputRef.current?.focus();
    }
  }, [loading]);

  async function sendMessage(text) {
    const msg = text.trim();
    if (!msg || loading) return;

    setStarted(true);
    setError('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, sessionId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur serveur');

      if (data.sessionId && data.sessionId !== sessionId) {
        setSessionId(data.sessionId);
        localStorage.setItem(SESSION_KEY, data.sessionId);
      }
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer ou nous écrire sur WhatsApp.');
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    sendMessage(input);
  }

  function resetChat() {
    setMessages([]);
    setStarted(false);
    setSessionId(null);
    localStorage.removeItem(SESSION_KEY);
  }

  const welcomeMessage = (
    <div className="flex justify-start mb-3">
      <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-xs shrink-0 mr-2 mt-1">
        A
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm max-w-[80%]">
        <p className="text-sm text-slate-800 leading-relaxed">
          Bonjour ! Je suis Axel, l'assistant IA d'Apex Rénovation.<br /><br />
          Je peux vous renseigner sur nos prestations, nos tarifs et nos délais d'intervention.
          Pour commencer, posez-moi votre question ou choisissez un sujet à gauche.
        </p>
      </div>
    </div>
  );

  const inputArea = (mobile = false) => (
    <div className={`border-t border-slate-100 ${mobile ? 'p-3' : 'p-4'} bg-white shrink-0`}>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          ref={mobile ? undefined : inputRef}
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Votre question…"
          disabled={loading}
          className="flex-1 text-sm border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 disabled:opacity-60 bg-slate-50"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="px-4 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-200 text-white rounded-xl font-semibold transition-colors shrink-0"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
          </svg>
        </button>
      </form>
      <p className="text-xs text-slate-400 mt-1.5 text-center">
        Réponses indicatives — devis précis après visite gratuite.
      </p>
    </div>
  );

  return (
    <>
      <PageSEO
        title="Assistant Axel — Posez vos questions | Apex Rénovation"
        description="Axel, l'assistant IA d'Apex Rénovation, répond à toutes vos questions sur vos travaux : tarifs, délais, prestations. Disponible 24h/24."
      />

      {/* ── Outer wrapper : fixed viewport on mobile, normal on desktop ── */}
      <div className="h-[100dvh] lg:h-auto lg:min-h-screen bg-slate-50 flex flex-col overflow-hidden lg:overflow-visible">

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="bg-slate-900 text-white shrink-0">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 lg:py-14">
            <div className="flex items-center gap-3 lg:gap-4 lg:mb-4">
              <div className="w-9 h-9 lg:w-14 lg:h-14 rounded-xl lg:rounded-2xl bg-orange-500 flex items-center justify-center text-white font-black text-base lg:text-2xl shrink-0">
                A
              </div>
              <div>
                <h1 className="text-base lg:text-3xl font-black tracking-tight">Axel</h1>
                <p className="text-slate-400 text-xs lg:text-sm mt-0.5">Assistant IA Apex Rénovation · disponible 24h/24</p>
              </div>
            </div>
            <div className="hidden lg:block">
              <p className="text-slate-300 text-lg leading-relaxed max-w-2xl mt-4">
                Posez vos questions sur vos travaux : tarifs, délais, prestations disponibles.
                Axel vous répond immédiatement. Pour un devis définitif, nous organisons
                ensuite une <strong className="text-white">visite gratuite</strong> chez vous.
              </p>
              <div className="mt-6 flex flex-wrap gap-3 text-sm text-slate-400">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-400 inline-block" />Répond en quelques secondes</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-orange-400 inline-block" />Zone Berry — Indre (36) & Cher (18)</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-400 inline-block" />Aussi disponible sur WhatsApp</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Mobile : quick questions strip ─────────────────────────── */}
        {!started && (
          <div className="lg:hidden bg-white border-b border-slate-200 px-3 py-2 shrink-0">
            <div className="flex gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {QUICK_QUESTIONS.map(q => (
                <button
                  key={q}
                  onClick={() => sendMessage(q)}
                  className="shrink-0 text-xs text-slate-700 bg-slate-50 hover:bg-orange-50 hover:text-orange-700 border border-slate-200 hover:border-orange-200 rounded-lg px-3 py-2 whitespace-nowrap transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Mobile chat (flex-1, no page scroll) ───────────────────── */}
        <div className="lg:hidden flex flex-col flex-1 overflow-hidden">
          <div ref={chatMobileRef} className="flex-1 overflow-y-auto px-4 py-4">
            {messages.length === 0 && !loading && welcomeMessage}
            {messages.map((msg, i) => <MessageBubble key={i} msg={msg} ref={i === messages.length - 1 && msg.role === 'assistant' ? lastMsgRef : null} />)}
            {loading && <TypingIndicator />}
            {error && (
              <div className="text-center py-3">
                <p className="text-red-500 text-sm bg-red-50 rounded-xl px-4 py-3 inline-block">{error}</p>
              </div>
            )}
          </div>
          {inputArea(true)}
          {started && (
            <div className="text-center pb-2 shrink-0">
              <button onClick={resetChat} className="text-xs text-slate-400 hover:text-slate-600 py-1 transition-colors">
                Recommencer la conversation
              </button>
            </div>
          )}
        </div>

        {/* ── Desktop : sidebar + chat grid ──────────────────────────── */}
        <div className="hidden lg:grid max-w-5xl mx-auto w-full px-6 py-8 grid-cols-3 gap-8 items-start">

          {/* Sidebar */}
          <div className="col-span-1 space-y-6">
            {!started && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                <h2 className="text-base font-bold text-slate-800 mb-4">Questions fréquentes</h2>
                <div className="space-y-2">
                  {QUICK_QUESTIONS.map(q => (
                    <button
                      key={q}
                      onClick={() => sendMessage(q)}
                      className="w-full text-left text-sm text-slate-700 bg-slate-50 hover:bg-orange-50 hover:text-orange-700 border border-slate-200 hover:border-orange-300 rounded-xl px-4 py-3 transition-colors leading-snug"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
              <h2 className="text-base font-bold text-slate-800 mb-4">Nous contacter par email</h2>
              <a
                href="mailto:contact@apexrenovation.fr"
                className="flex items-center gap-3 text-slate-700 hover:text-orange-600 group"
              >
                <div className="w-10 h-10 rounded-xl bg-orange-50 group-hover:bg-orange-100 flex items-center justify-center shrink-0 transition-colors">
                  <svg className="w-5 h-5 text-orange-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-base">contact@apexrenovation.fr</p>
                  <p className="text-xs text-slate-400">Réponse sous 24h</p>
                </div>
              </a>
            </div>

            {started && (
              <button onClick={resetChat} className="w-full text-sm text-slate-400 hover:text-slate-600 py-2 transition-colors">
                Recommencer la conversation
              </button>
            )}
          </div>

          {/* Desktop chat window */}
          <div className="col-span-2 flex flex-col">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col" style={{ minHeight: '520px' }}>
              <div ref={chatDesktopRef} className="flex-1 p-5 sm:p-6 overflow-y-auto" style={{ maxHeight: '560px' }}>
                {messages.length === 0 && !loading && welcomeMessage}
                {messages.map((msg, i) => <MessageBubble key={i} msg={msg} ref={i === messages.length - 1 && msg.role === 'assistant' ? lastMsgRef : null} />)}
                {loading && <TypingIndicator />}
                {error && (
                  <div className="text-center py-3">
                    <p className="text-red-500 text-sm bg-red-50 rounded-xl px-4 py-3 inline-block">{error}</p>
                  </div>
                )}
              </div>
              {inputArea(false)}
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
